import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Table,
  Header,
  Message,
  Form,
  Label,
  Loader,
} from "semantic-ui-react";
import axios from "../../../apis/dvdlocator";
import { AddSupplyUnitPage } from "./AddSupplyUnitPage";
import { FamilySearchField } from "./FamilySearchField";
import { PlatformListingFields } from "./PlatformListingFields";
import { useCurrentUserName } from "./useCurrentUserName";
import {
  EMPTY_IDENTIFIERS,
  LISTING_TITLE_MAX_LENGTH,
  buildListingPayload,
  hasIdentifierErrors,
  listingSubmitErrorMessage,
  platformLabel,
  validateListingTitle,
  validatePlatformIdentifiers,
  type ListingPlatform,
  type PlatformIdentifiers,
} from "./listingPayload";
import {
  PRODUCT_TITLE_MAX_LENGTH,
  productContactOptionsWith,
  validateProductTitle,
} from "./productFields";
import {
  buildInventoryRecords,
  buildNewProductBody,
  matchFromDuplicateCheck,
  sameProductMatch,
  selectedInventoryIds,
  type MatchedProduct,
  type ProductMatch,
  type RelatedListingSource,
} from "./relatedListing";
import {
  derivePartNumber,
  validateProductPartNumber,
} from "../../../validation/validateProductPartNumber";
import { isValidChildPartNumber } from "../../../validation/validateSupplyPartNumber";
import type {
  ProductData,
  WizardSupplyUnit,
} from "./AddSupplyUnitWizard/types";

interface CreateRelatedListingProps {
  source: RelatedListingSource;
  onClose: (didCreate?: boolean) => void;
}

const DUPLICATE_CHECK_DEBOUNCE_MS = 400;

const serverMessage = (err: any): string | null => {
  const message = err?.response?.data?.message;
  return typeof message === "string" ? message : null;
};

async function findProductWithInventory(
  inventoryIds: number[],
  signal?: AbortSignal
): Promise<MatchedProduct | null> {
  const res = await axios.get("/product/duplicate-check", {
    params: { inventoryIds: inventoryIds.join(",") },
    signal,
  });
  return res.data?.duplicateProduct ?? null;
}

/**
 * Create a listing for a subset (or any other combination) of a source
 * listing's inventory. When a product with exactly that inventory already
 * exists the listing is attached to it; otherwise a new product is created
 * first.
 */
export function CreateRelatedListing({
  source,
  onClose,
}: CreateRelatedListingProps) {
  // ─── Inventory ────────────────────────────────────────────────────────────
  const [supplyUnits, setSupplyUnits] = useState<WizardSupplyUnit[]>([]);
  const [partNumber, setPartNumber] = useState(source.partNumber);
  // Until the user edits Part Number, it follows the selected parts.
  const [partNumberTouched, setPartNumberTouched] = useState(false);

  // AddSupplyUnitPage loads GET /supply?productId= once for this product.
  const initialProduct = useMemo<ProductData | null>(
    () =>
      source.productId
        ? {
            product_id: source.productId,
            title: source.productTitle,
            partNumber: source.partNumber,
            families: source.families,
          }
        : null,
    [source]
  );

  useEffect(() => {
    if (partNumberTouched) return;
    const derived = derivePartNumber(supplyUnits);
    if (derived) setPartNumber(derived);
  }, [supplyUnits, partNumberTouched]);

  // ─── New product fields (used only when no product matches) ───────────────
  const [productTitle, setProductTitle] = useState(source.productTitle);
  const [families, setFamilies] = useState(source.families);
  const [productContact, setProductContact] = useState(source.productContact);
  const [productContactTouched, setProductContactTouched] = useState(false);

  // ─── Listing fields ───────────────────────────────────────────────────────
  const [platform, setPlatform] = useState<ListingPlatform>(source.platform);
  const [identifiers, setIdentifiers] =
    useState<PlatformIdentifiers>(EMPTY_IDENTIFIERS);
  const [listingTitle, setListingTitle] = useState(source.listingTitle);
  const createdBy = useCurrentUserName() ?? "";
  const [createdAt] = useState(() => new Date());

  // The person creating the listing is the default contact for a new product,
  // unless they've already picked someone
  useEffect(() => {
    if (createdBy && !productContactTouched) setProductContact(createdBy);
  }, [createdBy, productContactTouched]);

  // ─── Which product the listing goes to ────────────────────────────────────
  const inventoryIds = useMemo(
    () => selectedInventoryIds(supplyUnits),
    [supplyUnits]
  );
  const inventoryKey = inventoryIds.join(",");
  const [match, setMatch] = useState<ProductMatch>({ status: "idle" });
  const [recheckCount, setRecheckCount] = useState(0);

  useEffect(() => {
    if (!inventoryKey) {
      setMatch({ status: "idle" });
      return;
    }
    const ids = inventoryKey.split(",").map(Number);
    setMatch({ status: "checking", key: inventoryKey });
    const controller = new AbortController();
    const timer = setTimeout(() => {
      findProductWithInventory(ids, controller.signal)
        .then((product) =>
          setMatch(matchFromDuplicateCheck(inventoryKey, product))
        )
        .catch((err) => {
          if (controller.signal.aborted) return;
          setMatch({
            status: "error",
            key: inventoryKey,
            message: serverMessage(err) ?? "The request failed.",
          });
        });
    }, DUPLICATE_CHECK_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [inventoryKey, recheckCount]);

  // ─── Submit state ─────────────────────────────────────────────────────────
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // A product this form created, kept so a retry reuses it.
  const [createdProduct, setCreatedProduct] = useState<{
    id: number;
    inventoryLinked: boolean;
  } | null>(null);
  const [result, setResult] = useState<{
    productId: number;
    productTitle: string;
    newProduct: boolean;
  } | null>(null);

  // ─── Validation ───────────────────────────────────────────────────────────
  const willCreateProduct = match.status === "new";
  const listingTitleError = validateListingTitle(listingTitle);
  const identifierErrors = validatePlatformIdentifiers(platform, identifiers);
  const productTitleError = validateProductTitle(productTitle);
  const productContactError = productContact.trim()
    ? null
    : "Product Contact is required";
  const partNumberError = validateProductPartNumber(partNumber);
  const allUnitsFitPartNumber = supplyUnits.every(
    (unit) =>
      !partNumber || isValidChildPartNumber(partNumber, unit.part_number)
  );
  const newProductValid =
    !productTitleError &&
    !productContactError &&
    !partNumberError &&
    allUnitsFitPartNumber;
  const isValid =
    inventoryIds.length > 0 &&
    !listingTitleError &&
    !hasIdentifierErrors(identifierErrors) &&
    (!willCreateProduct || newProductValid);
  const matchReady = match.status === "existing" || match.status === "new";
  // Supply units without an inventory record can't be matched or linked.
  const unitsWithoutInventory = supplyUnits.filter(
    (unit) => unit.inventory_id === null || unit.inventory_id === undefined
  ).length;

  const handleSubmit = async () => {
    setSubmitted(true);
    setSubmitError(null);
    if (!isValid || !matchReady || isSubmitting) return;

    setIsSubmitting(true);
    let stage: "check" | "product" | "inventory" | "listing" = "check";
    let productId: number | null = null;
    let newProduct = false;
    try {
      // Don't trust the debounced result: check again right before writing.
      const freshMatch = matchFromDuplicateCheck(
        inventoryKey,
        await findProductWithInventory(inventoryIds)
      );
      if (!sameProductMatch(match, freshMatch)) {
        setMatch(freshMatch);
        setSubmitError(
          "Which product this inventory matches has changed since it was last checked. Review it below and submit again."
        );
        return;
      }

      let targetTitle = productTitle.trim();
      if (freshMatch.status === "existing") {
        productId = Number(freshMatch.product.product_id);
        targetTitle = freshMatch.product.product_title;
        newProduct = createdProduct?.id === productId;
      } else {
        newProduct = true;
        if (createdProduct && !createdProduct.inventoryLinked) {
          // An earlier attempt created the product but failed to link its
          // inventory. Reuse that product instead of creating another.
          productId = createdProduct.id;
        } else {
          stage = "product";
          const res = await axios.post(
            "/product",
            buildNewProductBody({
              title: productTitle,
              partNumber,
              families,
              createdBy: createdBy || "Unknown",
              productContact: productContact.trim(),
              createdOn: createdAt.toISOString(),
            })
          );
          productId = Number(res.data.id);
          setCreatedProduct({ id: productId, inventoryLinked: false });
        }

        stage = "inventory";
        await axios.post(`/product/${productId}/inventory`, {
          inventoryRecords: buildInventoryRecords(supplyUnits),
          clearExisting: false,
        });
        setCreatedProduct({ id: productId, inventoryLinked: true });
        // From here on the duplicate check finds this product, so a retry
        // after a failed listing POST attaches to it.
        setMatch({
          status: "existing",
          key: inventoryKey,
          product: { product_id: productId, product_title: targetTitle },
        });
      }

      stage = "listing";
      await axios.post(
        `/product/${productId}/listing`,
        buildListingPayload({
          platform,
          listingTitle,
          listingOrigin: "create_related_listing",
          createdBy,
          identifiers,
        })
      );
      setResult({ productId, productTitle: targetTitle, newProduct });
    } catch (err: any) {
      const reason = serverMessage(err);
      if (stage === "check") {
        setSubmitError(
          `Couldn't check for an existing product with this inventory${
            reason ? `: ${reason}` : ""
          }. Nothing was saved. Please try again.`
        );
      } else if (stage === "product") {
        setSubmitError(
          `Couldn't create the product${
            reason ? `: ${reason}` : ""
          }. Nothing was saved. Please try again.`
        );
      } else if (stage === "inventory") {
        setSubmitError(
          `Product #${productId} was created, but linking its inventory failed${
            reason ? `: ${reason}` : ""
          }. Submit again to link the inventory to product #${productId} and create the listing.`
        );
      } else if (newProduct) {
        setSubmitError(
          `Product #${productId} was created with this inventory, but creating the listing failed: ${listingSubmitErrorMessage(
            err
          )} Fix the listing if needed and submit again; the retry will reuse product #${productId}.`
        );
      } else {
        setSubmitError(listingSubmitErrorMessage(err));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const didCreate = !!result || !!createdProduct;
  const sourceProductLabel = source.productId
    ? `#${source.productId} ${source.productTitle}`.trim()
    : "No product";
  const contactOptions = useMemo(
    () => productContactOptionsWith(productContact, createdBy),
    [productContact, createdBy]
  );

  const renderMatch = () => {
    switch (match.status) {
      case "idle":
        return (
          <Message warning visible>
            Add at least one supply unit to continue.
          </Message>
        );
      case "checking":
        return (
          <Message>
            <Loader
              active
              inline
              size="tiny"
              style={{ marginRight: "0.75em" }}
            />
            Checking for a product with exactly this inventory…
          </Message>
        );
      case "existing":
        return (
          <Message positive>
            <Message.Header>
              Uses existing product #{match.product.product_id} —{" "}
              {match.product.product_title}
            </Message.Header>
            <p>
              A product with exactly this inventory already exists. The new
              listing will be attached to it.
            </p>
          </Message>
        );
      case "new":
        return (
          <Message info>
            <Message.Header>A new product will be created</Message.Header>
            <p>
              No product has exactly this inventory. Fill in the new product
              below; it will be created with the inventory above.
            </p>
          </Message>
        );
      case "error":
        return (
          <Message negative>
            <Message.Header>
              Couldn't check for an existing product
            </Message.Header>
            <p>{match.message}</p>
            <Button
              size="small"
              type="button"
              onClick={() => setRecheckCount((n) => n + 1)}
            >
              Retry
            </Button>
          </Message>
        );
    }
  };

  return (
    <Modal
      open
      onClose={() => onClose(didCreate)}
      closeOnDimmerClick={false}
      size="large"
      data-testid="create-related-listing"
    >
      <Modal.Header>Create Related Listing</Modal.Header>
      <Modal.Content>
        <Table definition compact>
          <Table.Body>
            <Table.Row>
              <Table.Cell width={4}>Source Listing</Table.Cell>
              <Table.Cell>
                {source.listingTitle || "—"} ({platformLabel(source.platform)})
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Source Product</Table.Cell>
              <Table.Cell>{sourceProductLabel}</Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>

        {result ? (
          <Message positive data-testid="create-related-listing-success">
            <Message.Header>
              Listing created and attached to product #{result.productId}.
            </Message.Header>
            <p>
              {result.newProduct ? "New product" : "Existing product"}: #
              {result.productId} {result.productTitle}
            </p>
          </Message>
        ) : (
          <div
            style={
              isSubmitting ? { pointerEvents: "none", opacity: 0.7 } : undefined
            }
          >
            {/* Inventory */}
            <Header as="h4" dividing style={{ marginTop: "1.5em" }}>
              Inventory
            </Header>
            <p style={{ color: "#666", fontSize: "0.9em" }}>
              Starts with the source product's inventory. Remove the parts the
              new listing doesn't include, or add others.
            </p>
            <AddSupplyUnitPage
              embedded
              product={{ title: productTitle, families, partNumber }}
              setProduct={(next) => {
                setPartNumber(next.partNumber ?? "");
                setPartNumberTouched(true);
              }}
              initialProduct={initialProduct}
              supplyUnits={supplyUnits}
              setSupplyUnits={setSupplyUnits}
            />

            {/* data-status lets the browser tests wait for the check's outcome */}
            <div data-testid="duplicate-check" data-status={match.status}>
              {renderMatch()}
            </div>
            {unitsWithoutInventory > 0 && (
              <Message warning visible>
                {unitsWithoutInventory} supply unit(s) have no inventory record.
                They are ignored when matching or creating the product.
              </Message>
            )}

            {/* New product (only when no product matches the inventory) */}
            {willCreateProduct && (
              <>
                <Header as="h4" dividing style={{ marginTop: "1.5em" }}>
                  New Product
                </Header>
                {submitted && partNumberError && (
                  <Message negative visible>
                    {partNumberError}
                  </Message>
                )}
                {!allUnitsFitPartNumber && (
                  <Message negative visible>
                    One or more supply units don't fit the Part Number{" "}
                    {partNumber}. Update the Part Number above.
                  </Message>
                )}
                <Form>
                  <Form.Field required>
                    <label>Product Title</label>
                    <Form.Input
                      placeholder="Enter Product Title"
                      value={productTitle}
                      onChange={(e) => setProductTitle(e.target.value)}
                      maxLength={PRODUCT_TITLE_MAX_LENGTH}
                    />
                    {submitted && productTitleError && (
                      <Label basic color="red" pointing>
                        {productTitleError}
                      </Label>
                    )}
                    {!productTitleError &&
                      !!source.productId &&
                      productTitle.trim() === source.productTitle.trim() && (
                        <Label basic pointing>
                          Same title as product #{source.productId}. Consider
                          naming the parts this product includes.
                        </Label>
                      )}
                  </Form.Field>
                  <Form.Field required>
                    <label>Product Contact</label>
                    <Form.Select
                      options={contactOptions}
                      value={productContact}
                      onChange={(_, data) => {
                        setProductContactTouched(true);
                        setProductContact(String(data.value ?? ""));
                      }}
                      placeholder="Select Product Contact"
                    />
                    {submitted && productContactError && (
                      <Label basic color="red" pointing>
                        {productContactError}
                      </Label>
                    )}
                  </Form.Field>
                  <FamilySearchField
                    families={families}
                    onChange={setFamilies}
                  />
                </Form>
              </>
            )}

            {/* Listing */}
            <Header as="h4" dividing style={{ marginTop: "1.5em" }}>
              New Listing
            </Header>
            {match.status === "existing" && (
              <p>
                This listing will be attached to product{" "}
                <strong>
                  #{match.product.product_id} {match.product.product_title}
                </strong>
                .
              </p>
            )}
            {match.status === "new" && (
              <p>This listing will be attached to the new product.</p>
            )}
            <Form>
              <PlatformListingFields
                platform={platform}
                onPlatformChange={(next) => {
                  setPlatform(next);
                  setSubmitError(null);
                }}
                identifiers={identifiers}
                onIdentifiersChange={(next) => {
                  setIdentifiers(next);
                  setSubmitError(null);
                }}
                showErrors={submitted}
              />
              <Form.Field required>
                <label>Listing Title</label>
                <Form.Input
                  placeholder="Enter Listing Title"
                  value={listingTitle}
                  onChange={(e) => {
                    setListingTitle(e.target.value);
                    setSubmitError(null);
                  }}
                  maxLength={LISTING_TITLE_MAX_LENGTH}
                />
                {submitted && listingTitleError && (
                  <Label basic color="red" pointing>
                    {listingTitleError}
                  </Label>
                )}
              </Form.Field>
              <Form.Field>
                <label>Created At</label>
                <Form.Input
                  disabled
                  value={createdAt.toLocaleString()}
                  readOnly
                />
              </Form.Field>
              <Form.Field>
                <label>Created By</label>
                <Form.Input disabled value={createdBy} readOnly />
              </Form.Field>
            </Form>

            {submitError && (
              <Message
                negative
                visible
                data-testid="create-related-listing-error"
              >
                <p>{submitError}</p>
              </Message>
            )}
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => onClose(didCreate)} disabled={isSubmitting}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result && (
          <Button
            primary
            onClick={handleSubmit}
            disabled={isSubmitting || !matchReady}
            loading={isSubmitting}
            data-testid="create-related-listing-submit"
          >
            Create Listing
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
}
